import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from 'react';

const SIDEBAR_STORAGE_KEY = 'sidebar-status';

type SidebarStatus = 'opened' | 'closed';

/** Get initial collapsed state from localStorage or default based on screen size */
const getInitialState = (): boolean => {
  if (typeof window === 'undefined') return false;

  const cachedSidebarStatus = localStorage.getItem(
    SIDEBAR_STORAGE_KEY
  ) as SidebarStatus | null;
  if (cachedSidebarStatus === 'opened') return false; // opened means not collapsed
  if (cachedSidebarStatus === 'closed') return true; // closed means collapsed

  // Desktop defaults to expanded (false = not collapsed)
  // Mobile defaults to collapsed (true = collapsed)
  const isMobile = window.innerWidth < 768;
  return isMobile;
};

interface SidebarStore {
  isCollapsed: boolean;
  setCollapsed: (collapsed: boolean) => void;
  toggleCollapsed: () => void;
}

const SidebarContext = createContext<SidebarStore | null>(null);

export function SidebarProvider({ children }: { children: ReactNode }) {
  const [isCollapsed, setIsCollapsed] = useState<boolean>(getInitialState);

  const setCollapsed = useCallback((collapsed: boolean) => {
    setIsCollapsed(collapsed);
    const status: SidebarStatus = collapsed ? 'closed' : 'opened';
    localStorage.setItem(SIDEBAR_STORAGE_KEY, status);
  }, []);

  const toggleCollapsed = useCallback(() => {
    setIsCollapsed((prev) => {
      const newState = !prev;
      const status: SidebarStatus = newState ? 'closed' : 'opened';
      localStorage.setItem(SIDEBAR_STORAGE_KEY, status);
      return newState;
    });
  }, []);

  const value: SidebarStore = {
    isCollapsed,
    setCollapsed,
    toggleCollapsed,
  };

  return (
    <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>
  );
}

export function useSidebarStore(): SidebarStore {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebarStore must be used within SidebarProvider');
  }
  return context;
}

// For compatibility with existing code that uses selector pattern
export function useSidebarStoreSelector<T>(
  selector: (state: SidebarStore) => T
): T {
  const store = useSidebarStore();
  return selector(store);
}
